// Copyright 2026 L3montree GmbH and the DevGuard Contributors.
// SPDX-License-Identifier: 	AGPL-3.0-or-later

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "../ui/form";
import type { GitLabIntegrationDTO } from "@/types/dto";
import { useForm } from "react-hook-form";
import { useLoader } from "@/hooks/useLoader";
import { saveGitlabIntegration } from "@/services/organizationService";
import { useActiveOrg } from "@/hooks/useActiveOrg";
import { toast } from "@/lib/toast";

import { Input } from "../ui/input";
import type { Surface } from "@/lib/surface";
import { Button } from "../ui/button";

export function urlToBaseURL(url: string): string {
  const regex = /^(https?:\/\/[^\/]+)/i; //regex rule https://regex101.com/r/n3xN3y/1
  const formatedUrl = url.split(regex);

  return formatedUrl[1];
}

export interface GitLabIntegrationFormProps {
  onNewIntegration: (integration: GitLabIntegrationDTO) => void;
  setOpen?: (open: boolean) => void;
  additionalOnClick?: () => void;
  backButtonClick?: () => void;
  // the surface the form is rendered on
  variant?: Surface;
}

export default function GitLabIntegrationForm({
  onNewIntegration,
  setOpen,
  additionalOnClick,
  backButtonClick,
  variant,
}: GitLabIntegrationFormProps) {
  const form = useForm<{ url: string; token: string; name: string }>();
  const activeOrg = useActiveOrg();
  const { Loader, waitFor, isLoading } = useLoader();

  const handleSubmit = async (params: {
    url: string;
    token: string;
    name: string;
  }) => {
    let integration;
    try {
      integration = await saveGitlabIntegration(activeOrg.slug, {
        ...params,
        url: urlToBaseURL(params.url),
      } as never);
    } catch {
      integration = null;
    }
    if (integration) {
      onNewIntegration(integration);
      if (setOpen) {
        setOpen(false);
      }
    } else {
      toast.error(
        "Your Gitlab/ openCode token seems to be wrong, check if the token has at least reporter access or is pasted correctly",
      );
    }
  };

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();

          await waitFor(form.handleSubmit(handleSubmit))();
          if (additionalOnClick) {
            additionalOnClick();
          }
        }}
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input
                  variant={variant}
                  data-testid="gitlab-pat-name"
                  placeholder="My GitLab Access Token"
                  autoComplete="url"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                A name to help identify the token later on.
              </FormDescription>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>GitLab Base URL</FormLabel>
              <FormControl>
                <Input
                  variant={variant}
                  data-testid="gitlab-base-url-input"
                  placeholder={
                    "https://gitlab.com/ or https://gitlab.opencode.de/"
                  }
                  autoComplete="url"
                  type="url"
                  required
                  {...field}
                />
              </FormControl>
              <FormDescription>
                Ensure that you provide{" "}
                <strong>only the base URL of your GitLab instance </strong>
                (e.g. https://gitlab.example.com) without any repository paths.
              </FormDescription>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="token"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Access-Token (Project or Personal Access Token)
              </FormLabel>
              <FormControl>
                <Input
                  variant={variant}
                  data-testid="gitlab-pat-input"
                  placeholder="glpat-xxxxxxxxxxx-xxxx"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                Use a <strong>Repository Access Token</strong> (recommended) or
                a Personal Access Token with the <strong>api scope</strong> and
                at least the <strong>Maintainer role</strong>.
                <br />
                To create one: GitLab Project →{" "}
                <strong>Settings → Access Tokens → Add new token</strong>.
              </FormDescription>
            </FormItem>
          )}
        />
        <div className="flex flex-row justify-end mt-4">
          <div className="flex flex-col items-end justify-end gap-2">
            <div className="flex flex-row gap-3">
              {backButtonClick && (
                <Button
                  type="button"
                  variant={"secondary"}
                  onClick={backButtonClick}
                >
                  Back
                </Button>
              )}
              <Button
                data-testid="gitlab-pat-save-button"
                disabled={isLoading}
                type="submit"
              >
                <Loader />
                Test and Save
              </Button>
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
}
